import React, { forwardRef } from 'react';

/**
 * Universal Button Component for Vocal for Local Design System
 * 
 * Strict compliance with Step 3 specifications:
 * - Variants: 'primary' | 'secondary' | 'ghost' | 'danger' | 'link'
 * - Sizes: 'sm' (32px, text 13px) | 'md' (40px, text 14px - default) | 'lg' (48px, text 16px)
 * - Polymorphic: supports `as` prop (e.g. NavLink, Link, a, button)
 * - Accessible: aria-busy when loading, mandatory aria-label when iconOnly
 * - Layout-shift free: loading spinner retains original dimensions
 */
export const Button = forwardRef(function Button(
  {
    as: Component = 'button',
    variant = 'primary',
    size = 'md',
    loading = false,
    disabled = false,
    icon: Icon = null,
    iconRight: IconRight = null,
    iconOnly = false,
    className = '',
    children,
    type = 'button',
    ...props
  },
  ref
) {
  const isButtonElement = Component === 'button';

  // Base styling for all variants
  const baseClasses =
    'relative inline-flex items-center justify-center font-medium select-none no-underline transition-colors duration-150 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--border-active)] focus-visible:ring-offset-2 disabled:opacity-45 disabled:pointer-events-none disabled:cursor-not-allowed cursor-pointer whitespace-nowrap active:scale-[0.99]';

  // Sizing maps
  const sizeClasses = {
    sm: iconOnly
      ? 'w-8 h-8 p-0 rounded-[var(--radius-md)] text-[13px]'
      : 'h-8 px-3 rounded-[var(--radius-md)] text-[13px] gap-2',
    md: iconOnly
      ? 'w-10 h-10 p-0 rounded-[var(--radius-md)] text-sm'
      : 'h-10 px-4 rounded-[var(--radius-md)] text-sm gap-2',
    lg: iconOnly
      ? 'w-12 h-12 p-0 rounded-[var(--radius-md)] text-base'
      : 'h-12 px-6 rounded-[var(--radius-md)] text-base gap-2.5',
  }[size] || 'h-10 px-4 rounded-[var(--radius-md)] text-sm gap-2';

  // Variant maps
  const variantClasses = {
    primary:
      'bg-[var(--accent)] text-[var(--on-accent)] hover:bg-[var(--accent-hover)] active:bg-[var(--accent-hover)] border border-transparent shadow-xs',
    secondary:
      'bg-transparent card-border-subtle text-[var(--text)] hover:bg-[var(--accent-soft)] hover:border-[var(--border-active)] active:bg-[var(--accent-soft)]',
    ghost:
      'bg-transparent text-[var(--text-muted)] hover:bg-[var(--surface-2)] hover:text-[var(--text)] border border-transparent',
    danger:
      'bg-[var(--danger)] text-white hover:opacity-90 active:opacity-100 border border-transparent shadow-xs',
    link:
      'bg-transparent text-[var(--accent)] hover:underline p-0 h-auto border-0 shadow-none active:scale-100',
  }[variant] || 'bg-[var(--accent)] text-[var(--on-accent)]';

  // Icon sizing
  const iconSizes = {
    sm: 'w-4 h-4',
    md: 'w-4.5 h-4.5',
    lg: 'w-5 h-5',
  }[size] || 'w-4.5 h-4.5';

  const isDisabled = disabled || loading;

  const componentProps = {
    ref,
    className: `${baseClasses} ${sizeClasses} ${variantClasses} ${className}`,
    'aria-busy': loading ? 'true' : undefined,
    'aria-disabled': isDisabled ? 'true' : undefined,
    ...(isButtonElement ? { type, disabled: isDisabled } : {}),
    ...props,
  };

  return (
    <Component {...componentProps}>
      {loading ? (
        <span className="inline-flex items-center justify-center gap-2">
          <svg
            className={`animate-spin text-current ${iconSizes}`}
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          {!iconOnly && <span className="opacity-80">{children}</span>}
        </span>
      ) : (
        <>
          {Icon && (
            <span className="shrink-0 flex items-center justify-center">
              {React.isValidElement(Icon) ? (
                Icon
              ) : (
                <Icon className={iconSizes} aria-hidden="true" />
              )}
            </span>
          )}
          {children && <span>{children}</span>}
          {IconRight && (
            <span className="shrink-0 flex items-center justify-center">
              {React.isValidElement(IconRight) ? (
                IconRight
              ) : (
                <IconRight className={iconSizes} aria-hidden="true" />
              )}
            </span>
          )}
        </>
      )}
    </Component>
  );
});

Button.displayName = 'Button';

export default Button;
